import {
  IHandleQuickSearchResult,
  ISearchAndFilterProduct,
  ISearchAndFilterProductImage,
  ISearchAndFilterProductPrice,
  IQuickSearchResultArticle,
  IQuickSearchResultCollection,
  IQuickSearchResultPage,
  IQuickSearchResultSuggestion,
  ISearchProviderConfig,
} from '../../../types';
import standardisePrice from '../../../../../util/standardisePrice';

/**
 * HandleSearch
 *
 * Uses Shopify Predictive Search JSON endpoint to provide quick search
 * suggestions and product results in the same shape expected by the
 * existing app-layer components.
 *
 * Endpoint docs:
 *   /search/suggest.json?resources[type]=product,page,article,collection
 */
const HandleSearch = (
  searchTerm: string,
  _config: ISearchProviderConfig,
): Promise<IHandleQuickSearchResult> =>
  new Promise<IHandleQuickSearchResult>((resolve) => {
    const response: IHandleQuickSearchResult = {
      raw: null,
      typos: [],
      products: [],
      collections: [],
      suggestions: [],
      pages: [],
      articles: [],
    };

    if (!searchTerm || searchTerm.trim() === '') {
      resolve(response);
      return;
    }

    try {
      const params = new URLSearchParams({
        q: searchTerm,
        'resources[type]': 'product,page,article,collection',
        // limits are intentionally conservative; if you want these to
        // respect config, wire config.qs*Limit through here.
        'resources[limit]': '10',
      });

      fetch(`/search/suggest.json?${params.toString()}`)
        .then((r) => r.json())
        .then((data) => {
          response.raw = data;

          const resources = data?.resources || {};

          // Map products
          if (Array.isArray(resources.products?.results)) {
            response.products = resources.products.results.map((product: any) => {
              const priceMin = standardisePrice(product.price_min || product.price);
              const priceMax = standardisePrice(product.price_max || product.price);
              const originalPrice = product.compare_at_price_max
                ? standardisePrice(product.compare_at_price_max)
                : priceMax;

              return <ISearchAndFilterProduct>{
                id: String(product.id),
                name: product.title,
                handle: product.handle,
                url: `https://${location.hostname}/products/${product.handle}`,
                images: (product.images || product.image ? [product.image] : []).map(
                  (image: any): ISearchAndFilterProductImage => ({
                    src: typeof image === 'string' ? image : image.src,
                  }),
                ),
                price: <ISearchAndFilterProductPrice>{
                  priceMin,
                  priceMax,
                  finalPrice: priceMax,
                  originalPrice,
                },
                meta: {},
                tags: product.tags || [],
                variants: product.variants || [],
              };
            });
          }

          // Map collections
          if (Array.isArray(resources.collections?.results)) {
            response.collections = resources.collections.results.map(
              (collection: any): IQuickSearchResultCollection => ({
                id: String(collection.id),
                title: collection.title,
                handle: collection.handle,
                url: `/collections/${collection.handle}`,
              }),
            );
          }

          // Map pages + articles from predictive search results
          if (Array.isArray(resources.pages?.results)) {
            resources.pages.results.forEach((page: any) => {
              const base = {
                id: String(page.id),
                handle: page.handle,
                title: page.title,
                url: page.url,
              };

              if (page.url && page.url.indexOf('/blogs/') === -1) {
                response.pages.push(<IQuickSearchResultPage>base);
              } else {
                response.articles.push(<IQuickSearchResultArticle>base);
              }
            });
          }

          // Map suggestions from the generic 'queries' collection if present
          if (Array.isArray(data?.results)) {
            response.suggestions = data.results.map(
              (item: any): IQuickSearchResultSuggestion => {
                const value = item?.terms || item?.title || '';
                return {
                  value,
                  query: String(value).replace(/ /g, '+'),
                };
              },
            );
          }

          resolve(response);
        })
        .catch((e) => {
          console.error(e);
          resolve(response);
        });
    } catch (e) {
      console.error(e);
      resolve(response);
    }
  });

export default HandleSearch;

